import { getBlockContent, type StreamingBlock } from "@storytell/streaming";
import { TbExternalLink } from "solid-icons/tb";
import { createMemo, Show } from "solid-js";
import { safeParseBlockJson } from "../../message/parseBlockContent";
import { useChatRuntime } from "../../runtime/ChatRuntime";
import { ScrollableFadeContainer } from "../../ui";
import { formatIsoTimestamp } from "../../util/dateFormat";
import { CompleteMarkdown } from "../internal/CompleteMarkdown";
import { LinearIcon } from "./linearIcon";
import { type LinearIssueDetail, parseLinearIssuePayload } from "./linearIssueSchema";
import { LinearIssueMeta, safeLinearUrl } from "./linearRenderUtils";

interface LinearIssueRendererProps {
  block: StreamingBlock;
}

export const LinearIssueRenderer = (props: LinearIssueRendererProps) => {
  const rt = useChatRuntime();

  const rawContent = createMemo(
    () =>
      (props.block.finalContent?.type === "text" ? props.block.finalContent.text : undefined) ??
      getBlockContent(props.block.blockId)
  );

  const payload = createMemo<LinearIssueDetail | null>(() => {
    const raw = rawContent();
    if (!raw) return null;
    const json = safeParseBlockJson(raw);
    if (!json) return null;
    return parseLinearIssuePayload(json);
  });

  const parseFailed = () => !!rawContent() && payload() === null;

  return (
    <Show
      when={!parseFailed()}
      fallback={
        <div class="rounded-lg border border-primary-border bg-panel px-3 py-2 text-xs text-muted-text">
          {rt.t("linear_issue_card.parse_error")}
        </div>
      }
    >
      <Show when={payload()}>
        {(data) => (
          <div class="overflow-hidden rounded-lg border border-primary-border bg-panel">
            <div class="border-b border-primary-border px-3 py-2">
              <div class="flex items-center gap-2">
                <LinearIcon class="size-4 shrink-0 text-primary-text" />
                <span class="shrink-0 font-mono text-xs text-muted-text">{data().identifier}</span>
                <Show
                  when={safeLinearUrl(data().url)}
                  fallback={<span class="truncate text-sm font-medium text-primary-text">{data().title}</span>}
                >
                  {(href) => (
                    <a
                      href={href()}
                      target="_blank"
                      rel="noopener noreferrer"
                      class="inline-flex min-w-0 items-center gap-1 text-sm font-medium text-brand hover:underline"
                    >
                      <span class="truncate">{data().title}</span>
                      <TbExternalLink class="size-3.5 shrink-0" aria-hidden="true" />
                    </a>
                  )}
                </Show>
              </div>
              <LinearIssueMeta issue={data()} />
            </div>

            <Show
              when={data().description?.trim()}
              fallback={<div class="p-3 text-sm text-muted-text">{rt.t("linear_issue_card.no_description")}</div>}
            >
              {(description) => (
                <div class="px-3 py-2">
                  <ScrollableFadeContainer maxHeight="200px">
                    <CompleteMarkdown markdown={description()} />
                  </ScrollableFadeContainer>
                </div>
              )}
            </Show>

            <div class="flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-primary-border px-3 py-2 text-xs text-muted-text">
              <Show when={formatIsoTimestamp(data().createdAt)}>
                {(date) => <span>{rt.t("linear_issue_card.created", { date: date() })}</span>}
              </Show>
              <Show when={formatIsoTimestamp(data().updatedAt)}>
                {(date) => <span>{rt.t("linear_issue_card.updated", { date: date() })}</span>}
              </Show>
              <span class="ml-auto">
                {data().commentCount === 1
                  ? rt.t("linear_issue_card.comments_one")
                  : rt.t("linear_issue_card.comments", { count: String(data().commentCount) })}
              </span>
            </div>
          </div>
        )}
      </Show>
    </Show>
  );
};
